/**
 * Formulario de los cinco bloques, en un drawer.
 *
 * Drawer y no modal: el patrón del panel es drawer para detalle y modal solo
 * para confirmar una acción. Acá se escribe durante varios minutos y la tabla de
 * atrás es el contexto de por qué se está escribiendo.
 *
 * Dos decisiones que importan:
 *
 *  - **Guardado parcial.** Se manda solo lo que cambió y con una sola respuesta
 *    ya se puede guardar. Una entrevista se carga en varias pasadas y perder lo
 *    escrito por no haber llegado a las 15 es la forma más rápida de que nadie
 *    las cargue.
 *  - **El error no cierra el drawer.** Si el PUT falla, el texto sigue en los
 *    campos, se muestra el mensaje de la API y se ofrece Reintentar. Cerrar en
 *    error tiraría a la basura una hora de conversación con un cliente.
 */
import {
  INTERVIEW_STATE_LABELS,
  type Interview,
  type InterviewBlockDef,
} from '@ops/contracts'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { mensajeDeError } from '../../lib/api'
import { formatFechaCorta, plural } from '../../lib/format'
import { useSaveInterviewAnswers } from '../../lib/queries'
import { rutaCliente } from '../../lib/rutas'
import { useVincular } from '../../lib/vincular'
import { useToast } from '../../shell/AppShell'
import {
  Button,
  Drawer,
  ExternalLink,
  Icon,
  ProgressBar,
  ProvenanceBadge,
  Rule,
  Stack,
  StatusPill,
  type Severidad,
} from '../../ui'
import { BloqueDePreguntas } from './BloqueDePreguntas'
import { borradorDe, cambiosDe, respondidasDe, type Borrador } from './borrador'
import { SEVERIDAD_POR_ESTADO, provenanceDeEntrevista } from './resumen'
import { type Vinculos } from './vinculos'

export interface FormularioEntrevistaProps {
  entrevista: Interview
  cuestionario: readonly InterviewBlockDef[]
  totalPreguntas: number
  vinculos: Vinculos
  now: Date
  onClose: () => void
}

export function FormularioEntrevista({
  entrevista,
  cuestionario,
  totalPreguntas,
  vinculos,
  now,
  onClose,
}: FormularioEntrevistaProps) {
  const original = borradorDe(entrevista.respuestas)
  const vincular = useVincular()
  const [borrador, setBorrador] = useState<Borrador>(original)
  const guardar = useSaveInterviewAnswers()
  const toast = useToast()

  const cambios = cambiosDe(borrador, original)
  const respondidas = respondidasDe(borrador)
  const mensajeError = guardar.isError ? mensajeDeError(guardar.error) : null

  // La barra refleja el borrador, no el estado guardado: si acabás de escribir
  // tres respuestas, la barra tiene que moverse antes de apretar Guardar.
  const severidadBorrador: Severidad =
    respondidas >= totalPreguntas
      ? 'ok'
      : respondidas > 0
        ? 'warning'
        : 'unknown'

  function onCambio(clave: string, valor: string) {
    setBorrador((actual) => ({ ...actual, [clave]: valor }))
  }

  function enviar() {
    if (cambios.length === 0) return
    guardar.mutate(
      { interviewId: entrevista.id, respuestas: cambios },
      {
        onSuccess: () => {
          toast.push(
            'ok',
            `Se guardaron ${cambios.length} ${plural(cambios.length, 'respuesta', 'respuestas')} de ${entrevista.clientNombre}`,
          )
          onClose()
        },
      },
    )
  }

  return (
    <Drawer
      open
      onClose={onClose}
      width="lg"
      title={`Entrevista · ${entrevista.clientNombre}`}
      subtitle={
        <>
          {INTERVIEW_STATE_LABELS[entrevista.estado]}
          {entrevista.fecha !== null
            ? ` · ${formatFechaCorta(entrevista.fecha)}`
            : ' · sin agendar'}
          {entrevista.entrevistador !== null
            ? ` · ${entrevista.entrevistador.nombre}`
            : ' · sin entrevistador asignado'}
        </>
      }
      footer={
        <>
          <span className="mr-auto text-micro text-muted-strong">
            {cambios.length === 0
              ? 'Nada nuevo para guardar'
              : `${cambios.length} ${plural(cambios.length, 'cambio', 'cambios')} sin guardar`}
          </span>
          <Button variante="sutil" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variante="primario"
            onClick={enviar}
            cargando={guardar.isPending}
            disabled={cambios.length === 0}
          >
            Guardar respuestas
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <StatusPill severidad={SEVERIDAD_POR_ESTADO[entrevista.estado]} size="sm">
            {INTERVIEW_STATE_LABELS[entrevista.estado]}
          </StatusPill>
          <Link
            to={rutaCliente(entrevista.clientId)}
            className="inline-flex items-center gap-1 text-meta font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
          >
            <Icon name="clientes" size={11} />
            Ficha de {entrevista.clientNombre}
          </Link>
          {vinculos.estado === 'listo' ? (
            <ExternalLink
              link={vinculos.closeDe(entrevista.clientId)}
              variante="chip"
              sistemaFaltante="Close"
              onVincular={() =>
                vincular(`el lead en Close de ${entrevista.clientNombre}`)
              }
              textoVincular="Pedir que se vincule"
            >
              Lead en Close
            </ExternalLink>
          ) : null}
        </div>

        <div className="flex flex-col gap-1">
          <ProgressBar
            pct={(respondidas / totalPreguntas) * 100}
            label={`Respuestas cargadas de ${entrevista.clientNombre}`}
            severidad={severidadBorrador}
            etiqueta={`${respondidas}/${totalPreguntas}`}
          />
          <p className="text-micro text-muted-strong">
            {respondidas} de {totalPreguntas} preguntas respondidas · {cuestionario.length}{' '}
            bloques de 3
          </p>
          <ProvenanceBadge
            provenance={provenanceDeEntrevista(entrevista, now)}
            now={now}
            variant="inline"
          />
        </div>

        <Rule punteada spacing="none" />

        {cuestionario.map((def) => (
          <BloqueDePreguntas
            key={def.bloque}
            def={def}
            borrador={borrador}
            onCambio={onCambio}
            deshabilitado={guardar.isPending}
          />
        ))}

        {mensajeError !== null ? (
          <div
            role="alert"
            data-testid="formulario-error"
            className="flex flex-col items-start gap-2 rounded-md border border-l-[3px] border-danger bg-danger-soft px-3 py-2 text-meta"
          >
            <span className="inline-flex items-start gap-1.5 font-semibold text-danger-text">
              <Icon name="critico" size={13} className="mt-px" />
              {mensajeError}
            </span>
            <span className="text-micro text-danger-text">
              Lo que escribiste sigue acá. Nada se perdió.
            </span>
            <Button
              variante="peligro"
              onClick={enviar}
              disabled={guardar.isPending}
            >
              Reintentar
            </Button>
          </div>
        ) : null}
      </Stack>
    </Drawer>
  )
}
